import { RunElement } from '../../shared/run-element.js';
import { skinResolutionMixin } from '../../shared/skin-resolution-mixin.js';
import * as defaultSkin from './skins/stack.default.js';

const DIRECTION_VALUES = ['vertical', 'horizontal'];
const ALIGN_VALUES = ['start', 'center', 'end', 'stretch', 'baseline'];
const JUSTIFY_VALUES = ['start', 'center', 'end', 'space-between', 'space-around', 'space-evenly'];

class RunStack extends skinResolutionMixin(RunElement) {
  #direction = 'vertical';
  #gap = 'md';
  #align = 'stretch';
  #justify = 'start';
  #wrap = false;

  static defaultSkin = defaultSkin;

  static skinLoaders = {
    wireframe: () => import('./skins/stack.wireframe.js'),
  };

  static get observedAttributes() {
    return ['direction', 'gap', 'align', 'justify', 'wrap'];
  }

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#updateAria();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'direction':  this.direction = newValue;          break;
      case 'gap':        if (newValue !== null) this.gap = newValue; break;
      case 'align':      this.align = newValue;              break;
      case 'justify':    this.justify = newValue;            break;
      case 'wrap':       this.wrap = newValue !== null;      break;
    }
  }

  get direction() { return this.#direction; }
  set direction(value) {
    const coerced = DIRECTION_VALUES.includes(String(value).trim())
      ? String(value).trim()
      : 'vertical';
    if (this.#direction === coerced) return;
    this.#direction = coerced;
    this.setAttribute('direction', coerced);
    this.requestUpdate();
  }

  get gap() { return this.#gap; }
  set gap(value) {
    const coerced = value == null ? 'md' : String(value).trim();
    if (this.#gap === coerced) return;
    this.#gap = coerced;
    if (coerced) {
      this.setAttribute('gap', coerced);
    } else {
      this.removeAttribute('gap');
    }
    this.requestUpdate();
  }

  get align() { return this.#align; }
  set align(value) {
    const coerced = ALIGN_VALUES.includes(String(value).trim())
      ? String(value).trim()
      : 'stretch';
    if (this.#align === coerced) return;
    this.#align = coerced;
    this.setAttribute('align', coerced);
    this.requestUpdate();
  }

  get justify() { return this.#justify; }
  set justify(value) {
    const coerced = JUSTIFY_VALUES.includes(String(value).trim())
      ? String(value).trim()
      : 'start';
    if (this.#justify === coerced) return;
    this.#justify = coerced;
    this.setAttribute('justify', coerced);
    this.requestUpdate();
  }

  get wrap() { return this.#wrap; }
  set wrap(value) {
    const coerced = Boolean(value);
    if (this.#wrap === coerced) return;
    this.#wrap = coerced;
    this.toggleAttribute('wrap', coerced);
    this.requestUpdate();
  }

  #updateAria() {
    this.setAttribute('role', 'group');
  }
}

if (!customElements.get('run-stack')) {
  customElements.define('run-stack', RunStack);
}

export { RunStack };
export default RunStack;